import {
  useOrganizationMediaScopeQuery,
  useUpdateOrganizationMediaScopeMutation,
} from "@decentriq/graphql/dist/hooks";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormControl, FormLabel, Input, Tooltip } from "@mui/joy";
import { memo, useCallback, useEffect, useState } from "react";
import { useOrganizationAdminConfiguration } from "features/admin/contexts";
import { CommonSnackbarOrigin, useGeneralSnackbar } from "hooks";
import { getEffectiveErrorMessage } from "utils";

const OrganizationMediaScopeEditor = memo(() => {
  const { organizationId, showMediaScopeEditor, enableMediaScopeEditor } =
    useOrganizationAdminConfiguration();
  const [mediaScope, setMediaScope] = useState<string | null>(null);

  const { data: organizationMediaScopeData } = useOrganizationMediaScopeQuery({
    variables: { organizationId },
  });

  const [updateOrganizationMediaScopeMutation] =
    useUpdateOrganizationMediaScopeMutation();

  useEffect(() => {
    const name = organizationMediaScopeData?.organization?.mediaScope;
    if (name) {
      setMediaScope(name);
    }
  }, [organizationMediaScopeData]);

  const { enqueueSnackbar } = useGeneralSnackbar({
    origin: CommonSnackbarOrigin.ADMIN,
  });

  const updateOrganizationMediaScope = useCallback(
    async (value: string | null) => {
      if (organizationMediaScopeData?.organization?.mediaScope === value)
        return;
      try {
        await updateOrganizationMediaScopeMutation({
          update: (cache, { data }) => {
            cache.modify({
              fields: {
                mediaScope: () => {
                  return data?.organization?.update?.mediaScope || null;
                },
              },
              id: cache.identify({
                __typename: "Organization",
                id: organizationId,
              }),
            });
          },
          variables: {
            input: {
              id: organizationId,
              mediaScope: { value },
            },
          },
        });
      } catch (error) {
        enqueueSnackbar("Organization name could not be updated.", {
          context: getEffectiveErrorMessage(error),
          persist: true,
          variant: "error",
        });
      }
    },
    [
      organizationMediaScopeData?.organization?.mediaScope,
      updateOrganizationMediaScopeMutation,
      organizationId,
      enqueueSnackbar,
    ]
  );

  const updateOrganizationMediaScopeOnEnter = useCallback(
    (event: any) => {
      const { target, keyCode } = event || {};
      if (keyCode !== 13 || !target.value) return;
      void updateOrganizationMediaScope(event.target.value);
    },
    [updateOrganizationMediaScope]
  );

  const clearMediaScope = useCallback(() => {
    void updateOrganizationMediaScope(null);
    setMediaScope(null);
  }, [updateOrganizationMediaScope]);

  const onMediaScopeChange = useCallback(
    (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      const trimmed = value.trim();
      if (trimmed) {
        void updateOrganizationMediaScope(trimmed);
      } else {
        clearMediaScope();
      }
    },
    [clearMediaScope, updateOrganizationMediaScope]
  );

  if (!showMediaScopeEditor) {
    return null;
  }

  return (
    <FormControl>
      <FormLabel>
        Media scope
        <span style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
          &nbsp;
          <Tooltip
            sx={{
              "& > .MuiBox-root": {
                maxHeight: "inherit",
              },
            }}
            title="Media scope allows controlling which publisher and data partner configurations are seen in the publisher and data partner selection screen. A user sees the following selection: All configurations of organisations without media scope plus all configurations of organisations with the identical media scope as the current user's organisation. Only real publishers & data partners should have no media scope set. Media scope 'demo'is used for organisations used for demos (only use it in discussion with the customer success team)."
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </Tooltip>
        </span>
      </FormLabel>
      <Input
        disabled={!enableMediaScopeEditor}
        name="mediaScope"
        onBlur={onMediaScopeChange}
        onChange={(target) => setMediaScope(target.target.value)}
        onKeyDown={updateOrganizationMediaScopeOnEnter}
        placeholder="Media scope"
        value={mediaScope || ""}
      />
    </FormControl>
  );
});

OrganizationMediaScopeEditor.displayName = "OrganizationMediaScopeEditor";

export default OrganizationMediaScopeEditor;
